import { useEffect, useState } from "react";
import { useDataProvider } from "react-admin";

// Cache por sessão das buscas de nome — o mesmo identificador aparece em
// várias linhas/telas e cada consulta só precisa acontecer uma vez.
const cache = new Map<string, Promise<string | null>>();

async function fetchName(
  dataProvider: any,
  identifier: string
): Promise<string | null> {
  const tryFilter = async (value: string) => {
    try {
      const res: any = await dataProvider.getList("users/crud", {
        pagination: { page: 1, perPage: 1 },
        sort: { field: "id", order: "ASC" },
        filter: { mailName: value },
      });
      const full = res?.data?.[0]?.fullName;
      return typeof full === "string" && full.trim() ? full.trim() : null;
    } catch {
      return null;
    }
  };
  let name = await tryFilter(identifier);
  if (!name) {
    // CPF pode estar salvo sem pontuação no cadastro — tenta só os dígitos
    const digits = identifier.replace(/\D/g, "");
    if (digits.length >= 11 && digits !== identifier) {
      name = await tryFilter(digits);
    }
  }
  return name;
}

// Nome completo do sócio a partir do identificador (e-mail ou CPF),
// usando a busca existente da tela de Sócios (filtro mailName cobre
// fullName/email/cpf no backend). Retorna null enquanto carrega ou
// quando não há cadastro correspondente.
export function useMemberName(identifier?: string): string | null {
  const dataProvider = useDataProvider();
  const [name, setName] = useState<string | null>(null);

  useEffect(() => {
    const trimmed = String(identifier || "").trim();
    const key = trimmed.toLowerCase();
    if (!key) {
      setName(null);
      return;
    }
    let cancelled = false;
    if (!cache.has(key)) {
      cache.set(key, fetchName(dataProvider, trimmed));
    }
    cache.get(key)!.then((n) => {
      if (!cancelled) setName(n);
    });
    return () => {
      cancelled = true;
    };
  }, [identifier, dataProvider]);

  return name;
}
